import { memo, useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import setTitle from '../../../components/common/SetTitle';
import { OutletIProps } from '../../../components/sitebase/Base.types';
import CartNotificationGroup from './CartNotificationGroup';
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import moment, { Moment } from 'moment';
import { NotificationsYearMonth } from '../notifications/notifications-list/NotificationsListSearch';
import type { CartNotificationData, EFASNotification } from '../notifications/Notification.types';


interface DeactivatedNotificationData extends EFASNotification {
    data_summary_parsed: CartNotificationData
}

const links = [
    { name: 'Cart', url: `/cart` },
    { name: 'Active Notifications', url: `/cart/active-notifications` },
    { name: 'Deactivated Notifications', url: `/cart/deactivated-notifications`, active: true },
];

export default memo(function DeactivatedNotificationsApp() {
    const { setExtraMenuLinks } = useOutletContext<OutletIProps>();
    const [notifications, setNotifications] = useState<DeactivatedNotificationData[]>([]);
    const [visibleNotifications, setVisibleNotifications] = useState<DeactivatedNotificationData[]>([]);
    const [partners, setPartners] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    // const searchParams = useSearchParams();

    const [date, setDate] = useState<Moment>(moment());
    const [partnerFilter, setPartnerFilter] = useState<string>("All");
    const [typeFilter, setTypeFilter] = useState<string>("All");

    const fetchNotificationsData = useCallback(() => {
        const _closing_date_start = date.clone().subtract(1, 'months').format('YYYY-MM-DD');
        const _closing_date = date.format('YYYY-MM-DD');

        setLoading(true);
        fetch(`/api/notifications/sent/?format=json&max=0&active=false&closing_date_start=${_closing_date_start}&closing_date=${_closing_date}&order_by=-closing_date&time=${Math.random()}`)
            .then((response) => {
                return response.json();
            }).then((data: EFASNotification[]) => {
                const data_summary_parsed = data.map(n => {
                    return {
                        ...n,
                        data_summary_parsed: JSON.parse(n.data_summary)
                    };
                });
                setNotifications(data_summary_parsed);
                setVisibleNotifications(data_summary_parsed);
            }).catch((error) => {
                console.error('Error fetching notifications data:', error);
            }).finally(() => {
                setLoading(false);
            });
    }, [date]);


    useEffect(() => {
        setTitle('Cart');
        window.scrollTo({ top: 0, behavior: 'instant' });
        setExtraMenuLinks(links);
    }, [setExtraMenuLinks]);

    useEffect(() => {
        fetchNotificationsData();
    }, [date, fetchNotificationsData]);

    useEffect(() => {
        if (notifications.length === 0) return;
        const uniquePartners = new Set<string>();
        notifications.forEach(n => uniquePartners.add(n.data_summary_parsed.partner?.abbreviation || "Unknown"));
        setPartners(Array.from(uniquePartners).sort());
    }, [notifications]);


    const filterNotificationsByPartner = (relevantNotifications: DeactivatedNotificationData[], partner: string) => {
        if (partner === "All") {
            return relevantNotifications;
        } else {
            return relevantNotifications.filter(n => n.data_summary_parsed.partner?.abbreviation === partner);
        }
    }

    const filterNotificationsByType = (relevantNotifications: DeactivatedNotificationData[], type: string) => {
        if (type === "Formal") {
            return relevantNotifications.filter(n => n.notification_type === 1);
        } else if (type === "Informal") {
            return relevantNotifications.filter(n => n.notification_type === 2);
        } else if (type === "Flash Flood") {
            return relevantNotifications.filter(n => n.notification_type === 3);
        } else { // All
            return relevantNotifications;
        }
    }

    const filterNotifications = (partner: string, type: string) => {
        let filteredNotifications = filterNotificationsByPartner(notifications, partner);
        filteredNotifications = filterNotificationsByType(filteredNotifications, type);
        return filteredNotifications;
    }

    return (<>
        <ToastContainer position='top-center' />
        <NotificationsYearMonth date={date} changeDate={setDate} />

        <div className='d-flex flex-row'>
            <div className="input-group">
                <label className="input-group-text">Partner</label>
                <select onChange={(e) => {
                    const partner = e.target.value;
                    setPartnerFilter(partner);
                    setVisibleNotifications(filterNotifications(partner, typeFilter));
                }}
                    className="form-select"
                    style={{ width: "10rem" }}>

                    <option>All</option>
                    {partners.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
            </div>
            <div className="input-group">
                <label className="input-group-text">Notification Type</label>
                <select onChange={(e) => {
                    const type = e.target.value;
                    setTypeFilter(type);
                    setVisibleNotifications(filterNotifications(partnerFilter, type));
                }}
                    style={{ width: "10rem" }}
                    className="form-select"
                >
                    <option>All</option>
                    <option>Formal</option>
                    <option>Informal</option>
                    <option>Flash Flood</option>
                </select>
            </div>
        </div>

        <div key={date.format('YYYY-MM-DD')} className='d-flex flex-column' id='deactivated-notifications-list'>
            {loading ? <div className='text-center py-5'><LoadingSpinner /></div> :
                visibleNotifications && visibleNotifications.length > 0 ? visibleNotifications.map((notification, index) =>
                    <CartNotificationGroup key={index} notification={notification} fetchNotificationsData={fetchNotificationsData} showClosedTimestamp />)
                    :
                    <div className='text-center py-5'>
                        {notifications.length === 0
                            ? 'No notifications for the selected month.'
                            : 'No notifications match the selected filters.'}
                    </div>
            }
        </div>
    </>);
})